import React from "react";
import { Box, Typography } from "@mui/material";
import location from "./resources/icons/location.svg";
import WorktypeChips from "./WorktypeChips";
import { worktypeMap, Dot, getHighestIconType, getImageSetForIconType } from "./components/work-type-images";
import apiClient from "./utils/apiClient";

// Export a function that accepts portals and ActionsMenuCell as parameters
export const getColumns = ({ portals, ActionsMenuCell, isMultiCompany, apiRef, updateRowById, setIsDownloading, setProgress, setStatus, setPopperOpen }) => [

  {
    field: "image",
    headerName: "",
    width: 124,
    sortable: false,
    filterable: false,
    colSpan: (params) => params?.row?.isSectionHeader ? 6 : 1,
    renderCell: (params) =>
      params.row.isSectionHeader ? (
        <div style={{
          position: "absolute",
          left: -50,
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          paddingLeft: 8,
          color: "#FFF",
          fontFamily: '"DM Sans"',
          fontSize: 14,
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "16px",
        }}>
          {params.row.label}
        </div>
      ) : params.row.isSkeleton ? (
        <div
          style={{
            width: 124,
            height: 72,
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: 4,
          }}
        />
      ) : (
        <div
          style={{
            position: "relative",
            width: "124px",
            height: "72px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingLeft: "0px",
          }}
        >
          <div
            style={{
              width: "1px",
              height: "72px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              marginRight: "0px",
              flexShrink: 0,
            }}
          />

          <div
            style={{
              position: "relative",
              width: "104px",
              height: "72px",
              overflow: "hidden",
              mixBlendMode: "screen",
            }}
          >
            {(() => {
              let src, srcSet;
              const iconType = getHighestIconType(params.row.worktypes);
              ({ src, srcSet } = getImageSetForIconType(iconType));
              return (
                <img
                  src={src}
                  srcSet={srcSet}
                  alt=""
                  draggable={false} // Voorkomt slepen van de afbeelding
                  onContextMenu={(e) => e.preventDefault()} // Blokkeert rechtermuisklik-contextmenu
                  onDragStart={(e) => e.preventDefault()} // Extra blokkade voor drag-events
                  style={{
                    width: "104px",
                    height: "72px",
                    objectFit: "cover",
                    mixBlendMode: "screen",
                    display: "block",
                    pointerEvents: 'none',
                  }}
                />
              );
            })()}
            <div
              className="dot-overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${Dot})`,
                backgroundRepeat: "repeat",
                backgroundSize: "2px 2px",
                // Scherpe stippen op retina i.p.v. uitgesmeerd
                imageRendering: "pixelated",
                opacity: 0.1,
                pointerEvents: "none",
              }}
            />
          </div>
          <div
            style={{
              width: "1px",
              height: "72px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              marginLeft: "0px",
              flexShrink: 0,
            }}
          />
        </div>
      ),
  },


  // Compacte layout (telefoon/iPad staand): één kolom met alles van een case
  // onder elkaar. Alleen zichtbaar in de compacte layout (columnVisibilityModel
  // in TransparentDataGrid); daar zijn de andere kolommen juist verborgen.
  {
    field: "mobileCase",
    headerName: "Case",
    flex: 1,
    minWidth: 0,
    sortable: false,
    filterable: false,
    // Enige zichtbare kolom in de compacte lijst (actions staat daar uit)
    renderCell: (params) => {
      const row = params.row;
      if (row.isSectionHeader) {
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#FFF", fontFamily: '"DM Sans"', fontSize: 14, fontWeight: 500, lineHeight: "16px" }}>
            {row.label}
          </div>
        );
      }
      if (row.isSkeleton) {
        return (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 8, height: "100%" }}>
            <div style={{ width: "60%", height: 16, background: "rgba(255, 255, 255, 0.1)", borderRadius: 4 }} />
            <div style={{ width: "40%", height: 14, background: "rgba(255, 255, 255, 0.1)", borderRadius: 4 }} />
            <div style={{ width: "50%", height: 20, background: "rgba(255, 255, 255, 0.1)", borderRadius: 4 }} />
          </div>
        );
      }
      const date = new Date(row.date);
      const isToday = date.toDateString() === new Date().toDateString();
      const when = isToday
        ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
        : date.toLocaleDateString("en-GB");
      const practice = [row.dentist, row.clinic && row.clinic !== row.dentist ? row.clinic : null].filter(Boolean).join(" • ");
      const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
      return (
        // paddingRight: de rechterrand van de rij valt op een telefoon net
        // buiten beeld (stond eerst het menuknopje), anders valt de datum weg
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6, height: "100%", width: "100%", minWidth: 0, paddingRight: 24, boxSizing: "border-box", cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
            <span style={{ ...ellipsis, flex: 1, minWidth: 0, color: "#FFF", fontFamily: "DM Sans", fontSize: 16, fontWeight: 500, lineHeight: "20px" }}>
              {row.patientName}
            </span>
            <span style={{ flexShrink: 0, color: isToday ? "#FFF" : "rgba(255, 255, 255, 0.6)", fontFamily: "DM Sans", fontSize: 14, fontWeight: isToday ? 500 : 400, lineHeight: "20px" }}>
              {when}
            </span>
          </div>
          {practice && (
            <span style={{ ...ellipsis, color: "rgba(255, 255, 255, 0.5)", fontFamily: "DM Sans", fontSize: 13, lineHeight: "16px" }}>
              {practice}
            </span>
          )}
          {/* Eén regel chips; wat niet past valt weg (de chips wrappen zelf) */}
          <div style={{ height: 24, overflow: "hidden", minWidth: 0 }}>
            <WorktypeChips
              row={row}
              worktypeMap={worktypeMap}
              brand={row.scannerBrand}
              portals={portals}
              receiver={row.receiver}
              location={location}
              isMultiCompany={isMultiCompany}
            />
          </div>
        </div>
      );
    },
  },
  {
    field: "dentistClinic",
    headerName: "Dentist / Clinic",
    flex: 38,
    minWidth: 180,
    maxWidth: 380,
    sortable: false,
    filterable: false,
    renderCell: (params) =>
      params.row.isSectionHeader ? null :
      params.row.isSkeleton ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            height: "100%",
          }}
        >
          <div
            style={{
              width: 200,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              width: 150,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "4px",
            height: "100%",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              color: "#FFF",
              fontFamily: "DM Sans",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={params.row.dentist || params.row.clinic}
          >
            {params.row.dentist || params.row.clinic}
          </span>
          {params.row.dentist && params.row.clinic && (
            <span
              style={{
                color: "rgba(255, 255, 255, 0.4)",
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={params.row.clinic || params.row.dentist}
            >
              {params.row.clinic}
            </span>
          )}
        </div>
      ),
  },
  {
    field: "patientInfo",
    headerName: "Patient Info",
    flex: 38,
    minWidth: 180,
    maxWidth: 380,
    sortable: false,
    filterable: false,
    renderCell: (params) =>
      params.row.isSectionHeader ? null :
      params.row.isSkeleton ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            height: "100%",
          }}
        >
          <div
            style={{
              width: 180,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              width: 120,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "4px",
            height: "100%",
          }}
        >
          <span
            style={{
              color: "#FFF",
              fontFamily: "DM Sans",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
            }}
          >
            {params.row.patientName}
          </span>
          <span
            style={{
              color: "rgba(255, 255, 255, 0.4)",
              fontFamily: "DM Sans",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            {params.row.birthday
              ? params.row.ref
                ? `${params.row.birthday} • ${params.row.ref}`
                : params.row.birthday
              : params.row.ref || ""}
          </span>
        </div>
      ),
  },
  {
    field: "scannerAndWorktype",
    headerName: "Scanner / Worktype",
    flex: 41,
    minWidth: 180,
    maxWidth: 436,
    sortable: false,
    renderCell: (params) =>
      params.row.isSectionHeader ? null :
      params.row.isSkeleton ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            height: "100%",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 100,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              width: 80,
              height: 16,
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
            }}
          />
        </div>
      ) : (
        <>
          <style>
            {portals[params.row.scannerBrand] &&
              `.hover-row-${params.id}:hover .scanner-chip-${params.id} { background-color: ${portals[params.row.scannerBrand].backgroundColor}; }`}
          </style>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "2px",
              height: "100%",
              minHeight: "36px",
            }}
          >


            <WorktypeChips
              row={params.row}
              worktypeMap={worktypeMap}
              brand={params.row.scannerBrand}
              portals={portals}
              receiver={params.row.receiver}
              location={location}
              isMultiCompany={isMultiCompany}
            />



          </Box>
        </>
      ),
  },
  {
    field: "dateTime",
    headerName: "Date / Time",
    width: 100,
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) =>
      params.row.isSectionHeader ? null :
      params.row.isSkeleton ? (
        <div
          style={{
            width: 80,
            height: 20,
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: 4,
            marginRight: 8,
          }}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "8px",
          }}
        >
          <Typography
            sx={{
              color: "#FFF",
              fontFamily: "DM Sans",
              fontStyle: "normal",
              fontWeight:
                new Date(params.row.date).toDateString() === new Date().toDateString()
                  ? 400
                  : 500,
              fontSize:
                new Date(params.row.date).toDateString() === new Date().toDateString()
                  ? "20px"
                  : "14px",
              lineHeight:
                new Date(params.row.date).toDateString() === new Date().toDateString()
                  ? "20px"
                  : "16px",
              textAlign: "right",
            }}
          >
            {new Date(params.row.date).toDateString() === new Date().toDateString()
              ? new Date(params.row.date).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              : new Date(params.row.date).toLocaleDateString("en-GB")}
          </Typography>
        </Box>
      ),
  },
  {
    field: "actions",
    headerName: "",
    width: 40,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) =>
      params.row.isSectionHeader ? null :
      params.row.isSkeleton ? (
        <div
          style={{
            width: 24,
            height: 36,
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: 8,
          }}
        />
      ) : (
        <ActionsMenuCell
          row={params.row}
          apiRef={apiRef}
          setIsDownloading={setIsDownloading}
          setProgress={setProgress}
          setStatus={setStatus}
          setPopperOpen={setPopperOpen}
        />
      ),
  },
  {
    // Lege marge rechts: de rijen staan 11px ingesprongen (padding-left van de
    // virtualScroller), dus zonder deze kolom komen de puntjes op smallere
    // schermen dichter bij de rechterrand dan de checkbox bij de linker.
    field: "edgeSpacer",
    headerName: "",
    width: 12,
    minWidth: 12,
    sortable: false,
    disableColumnMenu: true,
    renderCell: () => null,
  },
];